import moment from 'moment'

import { icons } from 'utils/icons'
import { IPost } from 'types/post.types'
import { POST_DATE_FORMATS } from 'utils/constants'
import { MONTHS, POST } from 'locales/locales'
import arTranslations from 'locales/ar/ar.json'
import enTranslations from 'locales/en/en.json'
import trTranslations from 'locales/tr/tr.json'

export const checkIfPostExists = (posts: IPost[], postId: string) => {
  return posts.some((post) => post._id === postId)
}

export const formatPostDate = (postDate: moment.Moment, notificationLanguage = 'tr') => {
  // Set moment locale based on notificationLanguage
  const locale = notificationLanguage === 'ar' ? 'ar' : notificationLanguage === 'en' ? 'en-gb' : 'tr'
  moment.locale(locale)
  
  const diffInDays = moment().endOf('day').diff(postDate, 'days')
  
  // Get translations based on language
  const getTranslation = (key: string) => {
    if (notificationLanguage === 'ar') {
      return (arTranslations as any)[key] || key
    } else if (notificationLanguage === 'en') {
      return (enTranslations as any)[key] || key
    } else {
      return (trTranslations as any)[key] || key
    }
  }
  
  // Custom date formatting based on language
  const formatDateWithLanguage = (date: moment.Moment, format: string) => {
    // Get translated month names based on language
    const getMonths = () => {
      const monthKeys = [
        MONTHS.JANUARY, MONTHS.FEBRUARY, MONTHS.MARCH, MONTHS.APRIL, MONTHS.MAY, MONTHS.JUNE,
        MONTHS.JULY, MONTHS.AUGUST, MONTHS.SEPTEMBER, MONTHS.OCTOBER, MONTHS.NOVEMBER, MONTHS.DECEMBER
      ]
      return monthKeys.map(key => getTranslation(key))
    }
    
    if (notificationLanguage === 'ar') {
      // Arabic date formatting
      if (format === 'LLL') {
        const day = date.date()
        const months = getMonths()
        const month = months[date.month()]
        const year = date.year()
        const time = date.format('HH:mm')
        return `${day} ${month} ${year} ${time}`
      }
    } else if (notificationLanguage === 'en') {
      // English date formatting
      if (format === 'LLL') {
        return date.format('MMM D, YYYY HH:mm')
      }
    } else {
      // Turkish date formatting
      if (format === 'LLL') {
        const day = date.date()
        const months = getMonths()
        const month = months[date.month()]
        const year = date.year()
        const time = date.format('HH:mm')
        return `${day} ${month} ${year} ${time}`
      }
    }
    
    // Fallback to default moment formatting
    return date.format(format)
  }
  
  // if posted today, print 'Today at post.sentAt'
  // if posted yesterday, print 'Yesterday at post.sentAt'
  // otherwise print the exact date 'DD/MM/YYYY HH:MM'
  if (diffInDays === 0) {
    return getTranslation(POST.TODAY_AT).concat(postDate.format(POST_DATE_FORMATS.POSTED_TODAY))
  } else if (diffInDays === 1) {
    return getTranslation(POST.YESTERDAY_AT).concat(postDate.format(POST_DATE_FORMATS.POSTED_YESTERDAY))
  }
  return formatDateWithLanguage(postDate, POST_DATE_FORMATS.POSTED_AT_LEAST_TWO_DAYS_AGO)
}

export const formatPostStatistic = (likeCount: number, notificationLanguage = 'tr') => {
  // Map notificationLanguage to locale for Intl.NumberFormat
  const locale = notificationLanguage === 'ar' ? 'ar' : notificationLanguage === 'en' ? 'en-US' : 'tr-TR'
  const formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 })
  if (likeCount < 1000) {
    return likeCount
  } else if (likeCount >= 1000 && likeCount < 1000 * 1000) {
    return formatter.format(likeCount / 1000) + 'k'
  } else if (likeCount >= 1000 * 1000) {
    return formatter.format(likeCount / (1000 * 1000)) + 'm'
  }
}

export const getThumbsDownIcon = (dislikes: string[], currentUserId: string) => {
  return dislikes?.includes(currentUserId) ? icons.thumbsDownIconActive : icons.thumbsDownIcon
}

export const getThumbsUpIcon = (likes: string[], currentUserId: string) => {
  return likes?.includes(currentUserId) ? icons.thumbsUpIconActive : icons.thumbsUpIcon
}

export const findLikeAndDislike = (isLike: boolean, post: IPost, myId: string) => {
  let likeData = []
  let dislikeData = []
  if (isLike) {
    if (post.likes.includes(myId)) {
      likeData = post.likes.filter((item: string) => item !== myId)
      dislikeData = [...post.dislikes]
    } else if (!post.likes.includes(myId) && post.dislikes.includes(myId)) {
      likeData = [...post.likes, myId]
      dislikeData = post.dislikes.filter((item: string) => item !== myId)
    } else {
      likeData = [...post.likes, myId]
      dislikeData = [...post.dislikes]
    }
  } else {
    if (post.dislikes.includes(myId)) {
      dislikeData = post.dislikes.filter((item: string) => item !== myId)
      likeData = [...post.likes]
    } else if (!post.dislikes.includes(myId) && post.likes.includes(myId)) {
      dislikeData = [...post.dislikes, myId]
      likeData = post.likes.filter((item: string) => item !== myId)
    } else {
      likeData = [...post.likes]
      dislikeData = [...post.dislikes, myId]
    }
  }

  return { likeData, dislikeData }
}
